import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatTooltip } from '@angular/material/tooltip';
import { NavigationEnd, Router, RouterLink } from '@angular/router';
import { filter, map, startWith } from 'rxjs';
import { CurrentUserService } from '@core/auth/current-user.service';
import { buildNavSections, NavLink } from '../nav';
import { NavIcon } from '../nav-icon/nav-icon';
import { ShellControls } from '../shell-controls/shell-controls';
import { SidebarNav } from '../sidebar-nav/sidebar-nav';
import { NotificationService } from '@core/notifications/notification.service';
import { HasRoleDirective } from '@shared/directives/has-role.directive';

interface CreateAction {
    readonly label: string;
    readonly path: string;
}

/**
 * Левая часть shell: логотип и название сервиса, компонент управления,
 * плашка главного раздела и навигационная панель. В свёрнутом виде
 * превращается в рейл с иконками.
 */
@Component({
    selector: 'app-sidebar',
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { '[class.sidebar--collapsed]': 'collapsed()' },
    imports: [MatTooltip, RouterLink, ShellControls, SidebarNav, NavIcon, HasRoleDirective],
    templateUrl: './sidebar.html',
    styleUrl: './sidebar.scss',
})
export class Sidebar {
    private readonly router = inject(Router);
    private readonly currentUser = inject(CurrentUserService);
    private readonly notifyService = inject(NotificationService);

    readonly collapsed = input(false);
    readonly userName = input.required<string>();
    readonly roleName = input<string | null>(null);

    readonly navToggled = output<void>();
    readonly logoutRequested = output<void>();

    private readonly navigationEnd = toSignal(
        this.router.events.pipe(
            filter((event): event is NavigationEnd => event instanceof NavigationEnd),
            map(() => this.router.url),
            startWith(this.router.url),
        ),
        { initialValue: this.router.url },
    );

    /** Главный раздел — отдельная плашка над навигацией, тоже из data.nav маршрутов. */
    protected readonly primary = computed<NavLink | null>(
        () =>
            buildNavSections(this.router.config, (roles) => this.currentUser.hasAnyRole(roles))
                .primary,
    );

    /** Кнопка «Создать...» берётся из data.nav.create текущего роута */
    protected readonly createAction = computed<CreateAction | null>(() => {
        this.navigationEnd();
        let current = this.router.routerState.snapshot.root;
        let basePath = '';

        while (current.firstChild) {
            current = current.firstChild;
            const routeConfig = current.routeConfig;
            const path = routeConfig?.path;
            if (path && path !== '' && path !== '**' && !path.includes(':')) {
                basePath += `/${path}`;
            }
            const nav = current.data?.['nav'] as { create?: string } | undefined;
            if (nav?.create) {
                const finalPath = basePath === '' ? '/' : basePath;
                return {
                    label: nav.create,
                    path: finalPath === '/' ? '/create' : `${finalPath}/create`,
                };
            }
        }

        return null;
    });
}
